import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  onSnapshot, 
  getDocs,
  Timestamp 
} from 'firebase/firestore';
import { db } from '../firebase';
import { RoomParticipant, Room } from '../types';

// Global map to track ongoing join operations per user-room combination
const ongoingJoins = new Map<string, Promise<string>>();

// Add user to room participants
export const joinRoomAsParticipant = async (
  roomId: string, 
  userId: string, 
  profile: {
    name: string;
    role: string;
    avatar: string;
  },
  isAdmin: boolean = false
): Promise<string> => {
  const joinKey = `${userId}-${roomId}`;
  
  // If there's already an ongoing join operation for this user-room, return that promise
  if (ongoingJoins.has(joinKey)) {
    // Join already in progress for this user-room combination, returning existing promise
    return ongoingJoins.get(joinKey)!;
  }

  // Create and store the join promise
  const joinPromise = performJoin(roomId, userId, profile, isAdmin);
  ongoingJoins.set(joinKey, joinPromise);
  
  try {
    const result = await joinPromise;
    return result;
  } finally {
    // Clean up the ongoing join tracking
    ongoingJoins.delete(joinKey);
  }
};

// Actual join implementation
const performJoin = async (
  roomId: string, 
  userId: string, 
  profile: { name: string; role: string; avatar: string; },
  isAdmin: boolean
): Promise<string> => {
  // Attempting to join user to room with profile
  
  // Check if user already exists in this room and remove all duplicates first
  const existingQuery = query(
    collection(db, 'roomParticipants'),
    where('roomId', '==', roomId),
    where('userId', '==', userId)
  );
  
  const existingSnapshot = await getDocs(existingQuery);
  // Found existing participants for user in room
  
  // Remove all existing entries for this user in this room to prevent duplicates
  if (!existingSnapshot.empty) {
    // Removing existing entries for user in room
    
    // Delete each document individually with error handling
    for (const docSnapshot of existingSnapshot.docs) {
      try {
        await deleteDoc(docSnapshot.ref);
        // Deleted existing participant document
      } catch (error) {
        // Error deleting participant document
      }
    }
    
    // Add a longer delay to ensure all deletions are complete
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // Double-check if there are still any remaining documents
    const recheckSnapshot = await getDocs(existingQuery);
    if (!recheckSnapshot.empty) {
      // Still found participants after deletion, forcing cleanup
      for (const docSnapshot of recheckSnapshot.docs) {
        try {
          await deleteDoc(docSnapshot.ref);
        } catch (error) {
          // Error in forced cleanup
        }
      }
      await new Promise(resolve => setTimeout(resolve, 300));
    }
  }

  // Create new participant
  const participantData: Omit<RoomParticipant, 'id'> = {
    userId,
    roomId,
    name: profile.name,
    role: profile.role as any,
    avatar: profile.avatar,
    isAdmin,
    joinedAt: new Date(),
    lastSeen: new Date()
  };

  const docRef = await addDoc(collection(db, 'roomParticipants'), participantData);
  // New participant created successfully
  
  // Check if this user should be the room admin (room creator)
  const isRoomCreator = localStorage.getItem('isRoomCreator') === 'true' || isAdmin;
  // Checking room creator flag for user
  
  if (isRoomCreator) {
    // First check if room already has an admin to prevent duplicates
    const { getDoc } = await import('firebase/firestore');
    const roomDoc = await getDoc(doc(db, 'rooms', roomId));
    const roomData = roomDoc.data();
    
    if (!roomData?.adminId || roomData.adminId === 'pending') {
      // Setting user as admin for room
      // Update room to set this user as admin
      await updateDoc(doc(db, 'rooms', roomId), {
        adminId: userId,
        lastActivity: new Date()
      });
      
      // Update participant to be admin
      await updateDoc(doc(db, 'roomParticipants', docRef.id), {
        isAdmin: true
      });
      
      // Successfully set admin
    } else {
      // Room already has admin, joining as regular user
      // Just update room activity
      await updateDoc(doc(db, 'rooms', roomId), {
        lastActivity: new Date()
      });
    }
    
    localStorage.removeItem('isRoomCreator');
  } else {
    // Update room's last activity
    await updateDoc(doc(db, 'rooms', roomId), {
      lastActivity: new Date()
    });
    // Updated room last activity for non-admin user
  }

  return docRef.id;
};

// Update user's last seen timestamp
export const updateUserLastSeen = async (participantId: string): Promise<void> => {
  await updateDoc(doc(db, 'roomParticipants', participantId), {
    lastSeen: new Date()
  });
};

// Remove user from room
export const leaveRoom = async (participantId: string, roomId: string, userId?: string): Promise<void> => {
  // Leave room function started
  // Parameters: participantId, roomId, userId
  
  let deleteSuccess = false;
  
  // First try to delete by participantId
  if (participantId) {
    try {
      await deleteDoc(doc(db, 'roomParticipants', participantId));
      // Successfully removed participant
      deleteSuccess = true;
    } catch (error) {
      // Error removing participant
    }
  }
  
  // If primary deletion failed and we have userId, try to find and delete all matching documents
  if (!deleteSuccess && userId && roomId) {
    // Primary deletion failed, attempting fallback deletion for userId in room
    try {
      const q = query(
        collection(db, 'roomParticipants'),
        where('roomId', '==', roomId),
        where('userId', '==', userId)
      );
      
      // Executing query to find participants
      const snapshot = await getDocs(q);
      // Query result: Found documents to delete for userId
      
      if (snapshot.docs.length > 0) {
        // Documents found for deletion
      }
      
      const deletePromises = snapshot.docs.map(async (docSnapshot) => {
        try {
          // Attempting to delete document
          await deleteDoc(docSnapshot.ref);
          // Successfully deleted participant document
        } catch (error) {
          // Error deleting document
        }
      });
      
      await Promise.all(deletePromises);
      deleteSuccess = snapshot.docs.length > 0;
      // Fallback deletion completed
    } catch (error) {
      // Error in fallback deletion
    }
  }
  
  if (!deleteSuccess) {
    // Failed to delete any participant documents
  } else {
    // Successfully deleted participant documents
  }
  
  // If this was an admin leaving, transfer admin role before cleanup
  if (deleteSuccess && userId && roomId) {
    // Checking for admin transfer
    await handleAdminTransferIfNeeded(roomId, userId);
  }
  
  // Check if room is now empty and delete if so
  // Checking for room cleanup
  await checkAndCleanupEmptyRoom(roomId);
  
  // Leave room function completed
};

// Handle admin transfer when admin leaves room
const handleAdminTransferIfNeeded = async (roomId: string, leavingUserId: string): Promise<void> => {
  try {
    // Check if the leaving user was the admin
    const { getDoc } = await import('firebase/firestore');
    const roomDoc = await getDoc(doc(db, 'rooms', roomId));
    const roomData = roomDoc.data();
    
    if (!roomData || roomData.adminId !== leavingUserId) {
      // User was not admin, no transfer needed
      return;
    }
    
    // Admin is leaving room, finding new admin
    
    // Find remaining participants in the room to transfer admin to
    const participantsQuery = query(
      collection(db, 'roomParticipants'),
      where('roomId', '==', roomId)
    );
    
    const participantsSnapshot = await getDocs(participantsQuery);
    const remainingParticipants = participantsSnapshot.docs;
    
    if (remainingParticipants.length === 0) {
      // No remaining participants, room will be deleted
      return;
    }
    
    // Sort by joinedAt to give admin to the earliest joiner
    const sortedParticipants = remainingParticipants
      .map(doc => ({ id: doc.id, ...doc.data() }) as RoomParticipant & { id: string })
      .sort((a, b) => {
        const dateA = a.joinedAt instanceof Timestamp ? a.joinedAt.toDate() : new Date(a.joinedAt);
        const dateB = b.joinedAt instanceof Timestamp ? b.joinedAt.toDate() : new Date(b.joinedAt);
        return dateA.getTime() - dateB.getTime();
      });
    
    const newAdmin = sortedParticipants[0];
    // Transferring admin to earliest joiner
    
    // Update room admin
    await updateDoc(doc(db, 'rooms', roomId), {
      adminId: newAdmin.userId,
      lastActivity: new Date()
    });
    
    // Update participant to be admin
    await updateDoc(doc(db, 'roomParticipants', newAdmin.id), {
      isAdmin: true
    });
    
    // Successfully transferred admin role
    
  } catch (error) {
    // Error during admin transfer
  }
};

// Check if room is empty and delete it
export const checkAndCleanupEmptyRoom = async (roomId: string): Promise<void> => {
  const q = query(
    collection(db, 'roomParticipants'),
    where('roomId', '==', roomId)
  );
  
  const snapshot = await getDocs(q);
  
  // If no participants, delete the room and all related data
  if (snapshot.empty) {
    // Room is empty, deleting
    
    // Delete room
    await deleteDoc(doc(db, 'rooms', roomId));
    
    // Delete all issues in the room
    const issuesQuery = query(
      collection(db, 'issues'),
      where('roomId', '==', roomId)
    );
    const issuesSnapshot = await getDocs(issuesQuery);
    const deleteIssuePromises = issuesSnapshot.docs.map(doc => deleteDoc(doc.ref));
    
    // Delete all voting sessions in the room
    const votingQuery = query(
      collection(db, 'votingSessions'),
      where('roomId', '==', roomId)
    );
    const votingSnapshot = await getDocs(votingQuery);
    const deleteVotingPromises = votingSnapshot.docs.map(doc => deleteDoc(doc.ref));
    
    await Promise.all([...deleteIssuePromises, ...deleteVotingPromises]);
    
    // Room and all related data deleted successfully
  }
};

// Cleanup old rooms (older than 4 days)
export const cleanupOldRooms = async (): Promise<void> => {
  const fourDaysAgo = new Date();
  fourDaysAgo.setDate(fourDaysAgo.getDate() - 4);
  
  const q = query(collection(db, 'rooms'));
  const snapshot = await getDocs(q);
  
  const deletePromises = snapshot.docs
    .filter(doc => {
      const room = doc.data() as Room;
      const createdAt = room.createdAt instanceof Timestamp 
        ? room.createdAt.toDate() 
        : new Date(room.createdAt);
      return createdAt < fourDaysAgo;
    })
    .map(async (roomDoc) => {
      const roomId = roomDoc.id;
      // Deleting old room
      
      // Delete all participants
      const participantsQuery = query(
        collection(db, 'roomParticipants'),
        where('roomId', '==', roomId)
      );
      const participantsSnapshot = await getDocs(participantsQuery);
      const deleteParticipantsPromises = participantsSnapshot.docs.map(doc => deleteDoc(doc.ref));
      
      // Delete all issues
      const issuesQuery = query(
        collection(db, 'issues'),
        where('roomId', '==', roomId)
      );
      const issuesSnapshot = await getDocs(issuesQuery);
      const deleteIssuePromises = issuesSnapshot.docs.map(doc => deleteDoc(doc.ref));
      
      // Delete all voting sessions
      const votingQuery = query(
        collection(db, 'votingSessions'),
        where('roomId', '==', roomId)
      );
      const votingSnapshot = await getDocs(votingQuery);
      const deleteVotingPromises = votingSnapshot.docs.map(doc => deleteDoc(doc.ref));
      
      // Delete room
      const deleteRoomPromise = deleteDoc(roomDoc.ref);
      
      await Promise.all([
        ...deleteParticipantsPromises,
        ...deleteIssuePromises,
        ...deleteVotingPromises,
        deleteRoomPromise
      ]);
    });
  
  await Promise.all(deletePromises);
  // Old rooms cleanup completed
};

// Get room participants
export const getRoomParticipants = (roomId: string, callback: (participants: RoomParticipant[]) => void) => {
  const q = query(
    collection(db, 'roomParticipants'),
    where('roomId', '==', roomId)
  );
  
  return onSnapshot(q, (snapshot) => {
    // Found participants for room
    const participants = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as RoomParticipant[];
    
    // Returning participants data
    callback(participants);
  }, (error) => {
    // Error getting room participants
  });
};

// Initialize cleanup service (to be called once when app starts)
export const initializeRoomCleanupService = (): void => {
  // Run cleanup every hour
  setInterval(cleanupOldRooms, 60 * 60 * 1000); // 1 hour
  
  // Run initial cleanup
  cleanupOldRooms();
};